import React from 'react';
import { PreviewRendererConfig, PreviewRendererProps } from './types';

// Import preview renderers
import CSVPreviewWrapper from './renderers/CSVPreviewWrapper';
import ImagePreviewRenderer from './renderers/ImagePreviewRenderer';
import VideoPreviewRenderer from './renderers/VideoPreviewRenderer';
import AudioPreviewRenderer from './renderers/AudioPreviewRenderer';
import TextPreviewRenderer from './renderers/TextPreviewRenderer';

/**
 * Registry of available preview renderers
 */
export const previewRenderers: PreviewRendererConfig[] = [
  {
    name: 'CSV Preview',
    supportedMimeTypes: ['text/csv'],
    supportedExtensions: ['.csv'],
    maxFileSize: 50 * 1024 * 1024, // 50MB limit for CSV files (increased limit)
    component: CSVPreviewWrapper,
  },
  {
    name: 'Image Preview',
    supportedMimeTypes: [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      'image/bmp',
    ],
    supportedExtensions: [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.svg',
      '.bmp',
    ],
    maxFileSize: 50 * 1024 * 1024, // 50MB limit for images
    component: ImagePreviewRenderer,
  },
  {
    name: 'Video Preview',
    supportedMimeTypes: [
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/avi',
      'video/quicktime',
    ],
    supportedExtensions: ['.mp4', '.webm', '.ogg', '.avi', '.mov'],
    maxFileSize: 100 * 1024 * 1024, // 100MB limit for videos
    component: VideoPreviewRenderer,
  },
  {
    name: 'Audio Preview',
    supportedMimeTypes: [
      'audio/mpeg',
      'audio/wav',
      'audio/ogg',
      'audio/flac',
      'audio/aac',
      'audio/webm',
    ],
    supportedExtensions: ['.mp3', '.wav', '.ogg', '.flac', '.aac', '.webm'],
    maxFileSize: 50 * 1024 * 1024, // 50MB limit for audio
    component: AudioPreviewRenderer,
  },
  {
    name: 'Text Preview',
    supportedMimeTypes: [
      'text/plain',
      'text/html',
      'text/css',
      'text/javascript',
      'text/typescript',
      'text/markdown',
      'text/xml',
      'application/json',
      'application/xml',
    ],
    supportedExtensions: [
      '.txt',
      '.html',
      '.css',
      '.js',
      '.ts',
      '.tsx',
      '.jsx',
      '.md',
      '.xml',
      '.json',
      '.py',
      '.rb',
      '.php',
      '.java',
      '.c',
      '.cpp',
      '.cs',
      '.go',
      '.rs',
      '.sql',
      '.yml',
      '.yaml',
    ],
    maxFileSize: 5 * 1024 * 1024, // 5MB limit for text files
    component: TextPreviewRenderer,
  },
];

/**
 * Find the appropriate preview renderer for a file
 */
export function findPreviewRenderer(
  filename: string,
  mimeType?: string,
  fileSize?: number
): PreviewRendererConfig | null {
  const extension = filename.toLowerCase().split('.').pop();
  const fileExtension = extension ? `.${extension}` : '';

  // Find renderer that supports this file type
  for (const renderer of previewRenderers) {
    const supportsMimeType =
      mimeType && renderer.supportedMimeTypes.includes(mimeType);
    const supportsExtension =
      renderer.supportedExtensions.includes(fileExtension);

    if (supportsMimeType || supportsExtension) {
      // Check file size limit if specified
      if (renderer.maxFileSize && fileSize && fileSize > renderer.maxFileSize) {
        continue; // Skip this renderer due to size limit
      }

      return renderer;
    }
  }

  return null;
}

/**
 * Check if a file can be previewed
 */
export function canPreviewFile(
  filename: string,
  mimeType?: string,
  fileSize?: number
): boolean {
  return findPreviewRenderer(filename, mimeType, fileSize) !== null;
}

/**
 * Get all supported file extensions
 */
export function getSupportedExtensions(): string[] {
  const extensions = new Set<string>();

  previewRenderers.forEach(renderer => {
    renderer.supportedExtensions.forEach(ext => extensions.add(ext));
  });

  return Array.from(extensions).sort();
}

/**
 * Get all supported MIME types
 */
export function getSupportedMimeTypes(): string[] {
  const mimeTypes = new Set<string>();

  previewRenderers.forEach(renderer => {
    renderer.supportedMimeTypes.forEach(mime => mimeTypes.add(mime));
  });

  return Array.from(mimeTypes).sort();
}

/**
 * Render the appropriate preview component for a file
 */
export function renderFilePreview(
  props: PreviewRendererProps
): React.ReactElement | null {
  const renderer = findPreviewRenderer(
    props.filename,
    props.mimeType,
    props.fileSize
  );

  if (!renderer) {
    return null;
  }

  const RendererComponent = renderer.component;
  return <RendererComponent {...props} />;
}
